import { useMemo } from 'react';
import { getUserStatus, useGetSettings, type UserEntry } from '../../hooks/useGetSettings';
import type { ActivityFeedUserOption } from './types';

function userDisplayName(user: UserEntry): string {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (name) return name;
    if (user.username?.trim()) return user.username.trim();
    return user.email;
}

/**
 * Org users for the activity-feed User filter. Excludes pending invites and
 * inactive accounts so the picker lists shop users who can appear as actors.
 * Internal/Yaxle staff are typically absent from this settings list; the
 * "Exclude Yaxle Admins" filter covers those actors server-side (and also
 * hides Carl-attributed rows per the activity-feed API).
 */
export function useActivityFeedFilterUsers(): ActivityFeedUserOption[] {
    const { data } = useGetSettings();

    return useMemo(() => {
        const userSection = data?.topSection?.sections?.find((s) => s.key === 'user') as
            | { data: UserEntry[] }
            | undefined;
        const users = userSection?.data ?? [];
        return users
            .filter((user) => getUserStatus(user) === 'active')
            .map((user) => ({
                id: user._id,
                label: userDisplayName(user),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [data]);
}
